import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import CircularLoader from './CircularLoader';
import { getAllItems } from '../services/api';

const FinalShowcase = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [tiles, setTiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const sectionRef = useRef(null);

  const normalizePhotoValue = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (trimmed.startsWith('data:image')) return trimmed;
    if (trimmed.startsWith('image/upload/https://') || trimmed.startsWith('image/upload/http://')) {
      return trimmed.replace(/^image\/upload\//, '');
    }
    return trimmed;
  };

  const pickPrimaryImage = (item) => {
    const candidateSingleRaw = item?.photo || item?.image;
    const candidateSingle = normalizePhotoValue(candidateSingleRaw);
    if (candidateSingle) return candidateSingle;

    const imgsRaw = item?.images || [];
    if (!Array.isArray(imgsRaw) || imgsRaw.length === 0) return '';

    const thumbObj = imgsRaw.find((x) => x && typeof x === 'object' && x.is_thumbnail && typeof x.image === 'string');
    if (thumbObj?.image) return normalizePhotoValue(thumbObj.image);

    for (const img of imgsRaw) {
      if (!img) continue;
      if (typeof img === 'string') return normalizePhotoValue(img);
      if (typeof img === 'object' && typeof img.image === 'string') return normalizePhotoValue(img.image);
    }
    return '';
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadTiles = async () => {
      try {
        if (!cancelled) setIsLoading(true);
        const res = await getAllItems();
        const items = res?.items || res || [];

        const mapped = Array.isArray(items)
          ? items
              .map((item) => {
                const image = pickPrimaryImage(item);
                if (!image) return null;
                return {
                  id: item.id,
                  name: item.name || 'New Arrival',
                  image,
                  category: item.category || '',
                  price: parseFloat(item.price) || 0,
                };
              })
              .filter(Boolean)
          : [];

        const sorted = [...mapped].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
        const picked = sorted.slice(0, 3);

        const withCopy = picked.map((p, idx) => {
          if (idx === 0) return { ...p, headline: p.name, theme: 'dark' };
          if (idx === 1) return { ...p, headline: p.name, theme: 'light' };
          return { ...p, headline: p.name, theme: 'teal' };
        });

        if (!cancelled) setTiles(withCopy);
      } catch (e) {
        if (!cancelled) setTiles([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadTiles();

    return () => {
      cancelled = true;
    };
  }, []);

  const Tile = ({ tile, index }) => {
    const isLight = tile.theme === 'light';
    const overlayOpacity = tile.theme === 'teal' ? 'opacity-60' : tile.theme === 'dark' ? 'opacity-80' : 'opacity-25';
    const bgFallback = tile.theme === 'teal' ? '#2d5a5a' : tile.theme === 'dark' ? '#4a5568' : '#e5e7eb';

    return (
      <div className="relative rounded-3xl overflow-hidden h-96" style={{ backgroundColor: bgFallback }}>
        <div
          className={`absolute inset-0 bg-cover bg-center ${overlayOpacity}`}
          style={{
            backgroundImage: `url('${tile.image}')`,
          }}
        />
        <div
          className={`absolute inset-0 ${
            isLight ? 'bg-white/40' : 'bg-black/25'
          }`}
        />

        <div className={`relative z-10 h-full flex flex-col justify-center items-center text-center ${
          isLight ? 'text-gray-900' : 'text-white'
        } p-8`}>
          <p
            className={`text-sm mb-2 ${isLight ? 'text-gray-700' : ''} transform transition-all duration-800 ease-out ${
              isVisible ? 'translate-y-0 opacity-100' : '-translate-y-8 opacity-0'
            }`}
            style={{ transitionDelay: `${index * 100}ms` }}
          >
            New Arrivals
          </p>

          <h2
            className={`text-3xl md:text-4xl font-bold mb-4 transform transition-all duration-800 ease-out ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
            style={{ transitionDelay: `${100 + index * 100}ms` }}
          >
            {tile.headline}
          </h2>

          {!!tile.category && (
            <p
              className={`text-sm mb-6 ${isLight ? 'text-gray-700' : 'text-white/90'} transform transition-all duration-800 ease-out ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: `${180 + index * 100}ms` }}
            >
              {tile.category}
            </p>
          )}

          <Link
            to={`/shop?productId=${encodeURIComponent(tile.id)}`}
            className={`bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-800 ease-out hover:scale-105 transform ${
              isVisible ? 'translate-y-0 opacity-100' : '-translate-y-12 opacity-0'
            }`}
            style={{ transitionDelay: `${240 + index * 100}ms` }}
          >
            Shop Now
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div ref={sectionRef} className="py-16 px-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {isLoading && tiles.length === 0 ? (
          <div className="py-10">
            <CircularLoader label="Loading featured arrivals..." size="lg" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tiles.map((tile, idx) => (
              <Tile key={tile.id || idx} tile={tile} index={idx} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinalShowcase;
